import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import type {
    NewMeetingLocationState,
    NewMeetingPrefill,
} from '@/types/thematic-meetings/newMeetingPrefill';

import { useNavigate } from '@/hooks/utils/useNavigate';
import { readNewMeetingPrefill } from '@/types/thematic-meetings/newMeetingPrefill';

/**
 * Opens the new-meeting window empty or already filled in: from the page's own button, from a row of
 * the overdue-employees dialog (which knows only the employee), or from another page's navigation
 * state (employee and topic). One prefill for every origin, so the window has a single source for its
 * defaults.
 *
 * A request carried by the history entry is acted on once and wiped from it: it describes an intent
 * already acted upon, so a reload or a step back lands on the plain list. The entry was written by
 * the host's router, which took the navigation out of the Growth tab; wiping it goes back the same
 * way, through `useNavigate`, because the module's own history does not reach the entry the host
 * holds -- the request would then reopen the wizard on every return to the list.
 */
export const useNewMeetingLauncher = () => {
    const navigate = useNavigate();
    const location = useLocation<NewMeetingLocationState | undefined>();

    const [isOpen, setIsOpen] = useState(false);
    const [prefill, setPrefill] = useState<Partial<NewMeetingPrefill> | undefined>(undefined);

    // Tracked by the state object itself: `readNewMeetingPrefill` hands back a fresh object on every
    // call, so acting on every run would set a new prefill each time and, whenever the navigation
    // function is not referentially stable, re-render into the same effect without end.
    const consumedStateRef = useRef<unknown>(undefined);
    useEffect(() => {
        if (location.state === consumedStateRef.current) {
            return;
        }
        consumedStateRef.current = location.state;

        const requestedPrefill = readNewMeetingPrefill(location.state);
        if (!requestedPrefill) {
            return;
        }

        setPrefill(requestedPrefill);
        setIsOpen(true);
        navigate({ path: location.pathname, replace: true });
    }, [location.state, location.pathname, navigate]);

    const open = (nextPrefill?: Partial<NewMeetingPrefill>) => {
        setPrefill(nextPrefill);
        setIsOpen(true);
    };

    const close = () => {
        setIsOpen(false);
        setPrefill(undefined);
    };

    return { isOpen, prefill, open, close };
};
